import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Bed, ShieldAlert, RefreshCw } from 'lucide-react';

export default function SleepoverSettingsPage() {
  const [flagOn, setFlagOn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    sleepover_flat_rate_aud: 0,
    active_hours_min_unit_hours: 1.0,
    active_hours_billing_mode: 'SEPARATE_LINE',
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const flags = await api.get('/production/feature-flags/mine').catch(() => ({ data: {} }));
      const flagsObj = flags.data?.data || flags.data || {};
      const enabled = !!flagsObj.sleepover_billing_enabled;
      setFlagOn(enabled);
      if (enabled) {
        const res = await api.get('/admin/settings/sleepover');
        const d = res.data?.data || {};
        setForm({
          sleepover_flat_rate_aud: d.sleepover_flat_rate_aud ?? 0,
          active_hours_min_unit_hours: d.active_hours_min_unit_hours ?? 1.0,
          active_hours_billing_mode: d.active_hours_billing_mode || 'SEPARATE_LINE',
        });
      }
    } catch (e) {
      toast.error('Failed to load settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleSave = async () => {
    if (form.sleepover_flat_rate_aud < 0 || form.active_hours_min_unit_hours < 0) {
      toast.error('Values must be non-negative');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/settings/sleepover', form);
      toast.success('Sleep-over settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-6 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="p-6 max-w-2xl space-y-4" data-testid="sleepover-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Bed className="w-5 h-5 text-slate-700" /> Sleep-Over Settings
        </h1>
        <p className="text-sm text-muted-foreground">Phase 1 — Data + Estimate only. Does NOT affect invoices, claims, or payroll.</p>
      </div>

      {!flagOn && (
        <Alert variant="default" className="border-amber-300 bg-amber-50">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>Feature flag disabled</AlertTitle>
          <AlertDescription>
            Enable <b>sleepover_billing_enabled</b> in <a href="/admin/settings/feature-flags" className="underline">Feature Flags</a> first to manage settings here.
          </AlertDescription>
        </Alert>
      )}

      {flagOn && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">Org-level rates <Badge variant="outline" className="text-[10px]">Phase 1</Badge></CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Sleep-Over Flat Rate (AUD)</Label>
              <Input type="number" min="0" step="0.01" value={form.sleepover_flat_rate_aud}
                onChange={e => setForm({ ...form, sleepover_flat_rate_aud: parseFloat(e.target.value || 0) })}
                data-testid="sleepover-flat-rate" />
              <p className="text-[10px] text-muted-foreground">Constant amount billed once per sleep-over shift. NEVER multiplied by hours.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Active Hours Minimum Unit (hours)</Label>
              <Input type="number" min="0" step="0.25" value={form.active_hours_min_unit_hours}
                onChange={e => setForm({ ...form, active_hours_min_unit_hours: parseFloat(e.target.value || 0) })}
                data-testid="sleepover-min-unit" />
              <p className="text-[10px] text-muted-foreground">Each woken interval rounds UP to this minimum (SCHADS-style). Default 1.0.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Active Hours Billing Mode</Label>
              <Select value={form.active_hours_billing_mode}
                onValueChange={v => setForm({ ...form, active_hours_billing_mode: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid="sleepover-billing-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SEPARATE_LINE">Separate line items (flat + active hours)</SelectItem>
                  <SelectItem value="BUNDLED">Bundled into a single line</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Alert className="border-amber-300 bg-amber-50">
              <AlertDescription className="text-[11px] text-amber-900">
                These settings drive the <b>read-only billing estimate</b> shown on Sleep-Over shifts in Calendar V3.
                Invoices, claims, and payroll are <b>not</b> affected in this phase.
              </AlertDescription>
            </Alert>
            <Button onClick={handleSave} disabled={saving} data-testid="sleepover-settings-save">
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
